/**
 * Admin Delivery Ledger Page
 * 
 * Read-only view of the Client Delivery Ledger.
 * Shows one card per client with clear, non-technical language
 * focused on expectations vs reality.
 */

import { useMemo } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  useClientLedger,
  useLedgerSummary,
  useLedgerWarnings
} from "@/contexts/ClientLedgerContext";
import { useMockAgents } from "@/contexts/MockAgentsContext";
import { 
  type LedgerSnapshot,
  type LedgerDeliveryStatus
} from "@/lib/clientDeliveryLedger";
import { 
  CheckCircle, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown,
  Package,
  Clock,
  RefreshCw,
  XCircle,
  Gift,
  AlertCircle,
  Users,
  Target,
  BarChart3,
  Shield
} from "lucide-react";
import { cn } from "@/lib/utils";

// --- Status Badge Component ---

function DeliveryStatusBadge({ status }: { status: LedgerDeliveryStatus }) {
  const config: Record<LedgerDeliveryStatus, { 
    label: string; 
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
    icon: React.ReactNode;
  }> = {
    ON_TRACK: { 
      label: 'On Track', 
      variant: 'default',
      icon: <CheckCircle className="h-3 w-3" />
    },
    UNDER_DELIVERED: { 
      label: 'At Risk', 
      variant: 'destructive',
      icon: <TrendingDown className="h-3 w-3" />
    },
    OVER_DELIVERED: { 
      label: 'Over-delivered', 
      variant: 'secondary',
      icon: <TrendingUp className="h-3 w-3" />
    },
    COMPLETE: { 
      label: 'Complete', 
      variant: 'outline',
      icon: <CheckCircle className="h-3 w-3" />
    },
  };
  
  const { label, variant, icon } = config[status];
  
  return (
    <Badge variant={variant} className="flex items-center gap-1">
      {icon}
      {label}
    </Badge>
  );
}

// --- Client Card Component ---

function ClientLedgerCard({ snapshot }: { snapshot: LedgerSnapshot }) {
  const _progressColor =
    snapshot.deliveryStatus === 'ON_TRACK' || snapshot.deliveryStatus === 'COMPLETE'
      ? 'bg-primary'
      : snapshot.deliveryStatus === 'UNDER_DELIVERED'
        ? 'bg-destructive'
        : 'bg-muted-foreground';
  
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{snapshot.clientName}</CardTitle>
            <CardDescription className="text-xs text-muted-foreground">
              Client ID: {snapshot.clientId}
            </CardDescription>
          </div>
          <DeliveryStatusBadge status={snapshot.deliveryStatus} />
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Main Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Delivery Progress</span>
            <span className="font-medium">
              {snapshot.totalDelivered} / {snapshot.totalPurchased} leads
            </span>
          </div>
          <Progress 
            value={snapshot.deliveryProgressPercent} 
            className="h-2"
          />
          <p className="text-xs text-muted-foreground text-right">
            {snapshot.deliveryProgressPercent.toFixed(1)}% complete
          </p>
        </div>
        
        <Separator />
        
        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Purchased</p>
              <p className="font-medium">{snapshot.totalPurchased}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Delivered</p>
              <p className="font-medium">{snapshot.totalDelivered}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Pending</p>
              <p className="font-medium">{snapshot.totalPending}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Retrying</p>
              <p className="font-medium">{snapshot.totalRetries}</p>
            </div>
          </div>
        </div>
        
        {/* Entitlement & Adjustments */}
        {(snapshot.remainingEntitlement > 0 || snapshot.totalCredited > 0 || snapshot.overDelivered > 0) && (
          <>
            <Separator />
            <div className="space-y-2">
              {snapshot.remainingEntitlement > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Target className="h-4 w-4" />
                    Remaining Entitlement
                  </span>
                  <span className="font-medium">{snapshot.remainingEntitlement} leads</span>
                </div>
              )}
              
              {snapshot.totalCredited > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Gift className="h-4 w-4" />
                    Credits Applied
                  </span>
                  <span className="font-medium text-primary">+{snapshot.totalCredited}</span>
                </div>
              )}
              
              {snapshot.overDelivered > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <TrendingUp className="h-4 w-4" />
                    Over-delivered
                  </span>
                  <span className="font-medium text-muted-foreground">+{snapshot.overDelivered}</span>
                </div>
              )}
              
              {snapshot.underDelivered > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-destructive">
                    <TrendingDown className="h-4 w-4" />
                    Under-delivered
                  </span>
                  <span className="font-medium text-destructive">-{snapshot.underDelivered}</span>
                </div>
              )}
            </div>
          </>
        )}
        
        {/* Active Contracts Summary */}
        {snapshot.activeContracts.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Active Contracts ({snapshot.activeContracts.length})
              </p>
              {snapshot.activeContracts.map(contract => (
                <div 
                  key={contract.contractId}
                  className="flex items-center justify-between text-xs bg-muted/50 rounded px-2 py-1.5"
                >
                  <span className="capitalize">{contract.contractType.replace('_', ' ')}</span>
                  <span className="text-muted-foreground">
                    {contract.delivered}/{contract.totalCommitted} • {contract.remainingDays}d left
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
        
        {/* Data Warnings */}
        {snapshot.dataWarnings.length > 0 && (
          <>
            <Separator />
            <div className="space-y-1">
              {snapshot.dataWarnings.map((warning, i) => (
                <div 
                  key={i}
                  className={cn(
                    "flex items-start gap-2 text-xs rounded px-2 py-1.5",
                    warning.severity === 'error' && "bg-destructive/10 text-destructive",
                    warning.severity === 'warn' && "bg-yellow-500/10 text-yellow-600",
                    warning.severity === 'info' && "bg-muted text-muted-foreground"
                  )}
                >
                  <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                  <span>{warning.message}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// --- Summary Card Component ---

function LedgerSummaryCard() {
  const summary = useLedgerSummary();
  
  if (!summary) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading ledger data...
        </CardContent>
      </Card>
    );
  }
  
  const healthColor = 
    summary.systemHealthScore >= 80 ? 'text-primary' :
    summary.systemHealthScore >= 60 ? 'text-yellow-500' :
    'text-destructive';
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          Delivery Ledger Summary
        </CardTitle>
        <CardDescription>
          System-wide client delivery health
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <Users className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
            <p className="text-2xl font-bold">{summary.totalClients}</p>
            <p className="text-xs text-muted-foreground">Total Clients</p>
          </div>
          
          <div className="text-center p-3 bg-primary/10 rounded-lg">
            <CheckCircle className="h-5 w-5 mx-auto mb-1 text-primary" />
            <p className="text-2xl font-bold text-primary">{summary.onTrackClients}</p>
            <p className="text-xs text-muted-foreground">On Track</p>
          </div>
          
          <div className="text-center p-3 bg-destructive/10 rounded-lg">
            <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-destructive" />
            <p className="text-2xl font-bold text-destructive">{summary.underDeliveredClients}</p>
            <p className="text-xs text-muted-foreground">At Risk</p>
          </div>
          
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <Shield className={cn("h-5 w-5 mx-auto mb-1", healthColor)} />
            <p className={cn("text-2xl font-bold", healthColor)}>{summary.systemHealthScore}%</p>
            <p className="text-xs text-muted-foreground">Health Score</p>
          </div>
        </div>
        
        <Separator className="my-4" />
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Total Leads Purchased</span>
          <span className="font-medium">{summary.totalPurchasedAcrossAll.toLocaleString()}</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-1">
          <span className="text-muted-foreground">Total Leads Delivered</span>
          <span className="font-medium">{summary.totalDeliveredAcrossAll.toLocaleString()}</span>
        </div>
      </CardContent>
    </Card>
  );
}

// --- System Warnings Component ---

function SystemWarningsCard() {
  const warnings = useLedgerWarnings();
  
  if (warnings.length === 0) {
    return null;
  }
  
  const errorCount = warnings.filter(w => w.severity === 'error').length;
  const warnCount = warnings.filter(w => w.severity === 'warn').length;
  
  return (
    <Card className="border-yellow-500/50">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-yellow-600">
          <AlertTriangle className="h-5 w-5" />
          Data Consistency Warnings
        </CardTitle>
        <CardDescription>
          {errorCount > 0 && <span className="text-destructive">{errorCount} errors</span>}
          {errorCount > 0 && warnCount > 0 && ' • '}
          {warnCount > 0 && <span className="text-yellow-600">{warnCount} warnings</span>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {warnings.slice(0, 5).map((warning, i) => (
          <div 
            key={i}
            className={cn(
              "flex items-start gap-2 text-sm rounded px-3 py-2",
              warning.severity === 'error' && "bg-destructive/10 text-destructive",
              warning.severity === 'warn' && "bg-yellow-500/10 text-yellow-600",
              warning.severity === 'info' && "bg-muted text-muted-foreground"
            )}
          >
            {warning.severity === 'error' ? (
              <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
            ) : (
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            )}
            <span>{warning.message}</span>
          </div>
        ))}
        {warnings.length > 5 && (
          <p className="text-xs text-muted-foreground text-center pt-2">
            + {warnings.length - 5} more warnings
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// --- Main Page Component ---

export default function AdminDeliveryLedger() {
  const { state, actions } = useClientLedger();
  const { clientAgents } = useMockAgents();
  const clientIds = useMemo(() => new Set(clientAgents.map((a) => a.id)), [clientAgents]);
  const snapshots = useMemo(
    () => actions.getAllSnapshots().filter((s) => clientIds.has(s.clientId)),
    [actions, clientIds]
  );
  
  // Sort: at-risk first, then by remaining entitlement
  const sortedSnapshots = [...snapshots].sort((a, b) => {
    const statusOrder: Record<LedgerDeliveryStatus, number> = {
      UNDER_DELIVERED: 0,
      ON_TRACK: 1,
      OVER_DELIVERED: 2,
      COMPLETE: 3,
    };
    
    const statusDiff = statusOrder[a.deliveryStatus] - statusOrder[b.deliveryStatus];
    if (statusDiff !== 0) return statusDiff;
    
    return b.remainingEntitlement - a.remainingEntitlement;
  });
  
  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Delivery Ledger</h1>
          <p className="text-muted-foreground">
            Single source of truth for client entitlements and delivery status
          </p>
          {state.lastComputedAt && (
            <p className="text-xs text-muted-foreground mt-1">
              Last computed: {new Date(state.lastComputedAt).toLocaleTimeString()}
            </p>
          )}
        </div>
        
        {/* Summary */}
        <LedgerSummaryCard />
        
        {/* Warnings */}
        <SystemWarningsCard />
        
        {/* Client Cards */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Client Ledgers</h2>
          
          {state.isComputing ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                Computing ledger snapshots...
              </CardContent>
            </Card>
          ) : sortedSnapshots.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No client ledgers to display</p>
                <p className="text-xs mt-1">Create contracts to see client delivery tracking</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {sortedSnapshots.map(snapshot => (
                <ClientLedgerCard key={snapshot.clientId} snapshot={snapshot} />
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
